/**
 * ModalShell — Wrapper React a11y pour toutes les modals DA.
 *
 * Compose ModalOverlay + ModalCard + les 3 hooks a11y (focusTrap, escClose, scrollLock).
 * C'est le point d'accroche unique pour l'accessibilité des modals.
 *
 * §11/§18 DA — toute modal DOIT composer ModalShell plutôt qu'assembler
 * ModalOverlay + ModalCard manuellement.
 * §12 sexies — la croix de fermeture (ModalCloseBtn) reste à la charge
 * de chaque modal dans son ModalHeader (logique métier propre).
 *
 * Migration : les modals existantes migrent vers ModalShell progressivement (P1-P4).
 * Les 10 modals DA actuelles fonctionnent toujours via les primitives brutes.
 *
 * @prop {boolean}    isOpen    — monter/démonter la modal
 * @prop {() => void} onClose   — callback de fermeture (Echap + clic overlay)
 * @prop {string}     [$width]  — largeur de la ModalCard (défaut: '30rem')
 * @prop {string}     [role]    — rôle ARIA (défaut: 'dialog')
 * @prop {string}     [aria-label] — libellé ARIA (recommandé)
 * @prop {React.ReactNode} children — contenu (ModalHeader, ModalBody, ModalFooter…)
 *
 * @example
 *   <ModalShell isOpen={isOpen} onClose={handleClose} $width="36rem" aria-label="Retrait de fonds">
 *     <ModalHeader>…</ModalHeader>
 *     <ModalBody>…</ModalBody>
 *     <ModalFooter>…</ModalFooter>
 *   </ModalShell>
 */
import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { ModalOverlay, ModalCard } from './ModalDA';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscClose } from '../../hooks/useEscClose';
import { useScrollLock } from '../../hooks/useScrollLock';

export function ModalShell({ isOpen, onClose, $width, role = 'dialog', children, ...ariaProps }) {
  const cardRef = useRef(null);

  useFocusTrap(cardRef, isOpen);
  useEscClose(isOpen, onClose);
  useScrollLock(isOpen);

  if (!isOpen) return null;

  function handleOverlayClick(e) {
    /* Ferme seulement si clic sur l'overlay lui-même (pas sur la card) */
    if (e.target === e.currentTarget) onClose();
  }

  /* §69 — Portal vers document.body : l'overlay (position:fixed; z-index:9999) est rendu
     hors de l'arbre de la page courante. Sur une page normale le rendu est identique ; en
     mode fullpage (AssetFullPage : 100vh overflow:hidden + enfants backdrop-filter = stacking
     context / containing-block), le portal garantit que la modal n'est jamais clippée ni piégée
     par un ancêtre. Un seul lieu corrigé → les 29 consommateurs de ModalShell en bénéficient (DRY §9). */
  return createPortal(
    <ModalOverlay
      role={role}
      aria-modal="true"
      onClick={handleOverlayClick}
      {...ariaProps}
    >
      <ModalCard ref={cardRef} $width={$width}>
        {children}
      </ModalCard>
    </ModalOverlay>,
    document.body
  );
}

ModalShell.propTypes = {
  isOpen:   PropTypes.bool.isRequired,
  onClose:  PropTypes.func.isRequired,
  $width:   PropTypes.string,
  role:     PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default ModalShell;
